class WDrawer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        if (event.target === this) {
          this.close();
          return;
        }
        if (event.target.closest('[data-w-drawer-close]')) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.trapController?.abort();
    this.trapController = null;
  }

  open(trigger) {
    if (this.isOpen) return;
    this.opener = trigger || document.activeElement;
    clearTimeout(this.hideTimer);
    this.removeAttribute('hidden');
    void this.offsetHeight;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('w-scroll-lock');

    const panel = this.querySelector('.w-drawer__panel');
    const target = this.querySelector('[data-w-drawer-close]') || panel;
    if (target) {
      if (target === panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      target.focus();
    }

    this.trapController = new AbortController();
    document.addEventListener('keydown', (event) => this.trapFocus(event), {
      signal: this.trapController.signal,
    });

    this.dispatchEvent(new CustomEvent('w-drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('w-scroll-lock');

    this.trapController?.abort();
    this.trapController = null;

    const panel = this.querySelector('.w-drawer__panel');
    const finalize = () => {
      clearTimeout(this.hideTimer);
      if (!this.isOpen) this.setAttribute('hidden', '');
    };
    if (panel) {
      panel.addEventListener('transitionend', finalize, { once: true });
      this.hideTimer = setTimeout(finalize, 400);
    } else {
      finalize();
    }

    if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    this.opener = null;

    this.dispatchEvent(new CustomEvent('w-drawer:close', { bubbles: true }));
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusables = this.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const panel = this.querySelector('.w-drawer__panel');
    if (event.shiftKey && (active === first || active === panel || !this.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }
}

window.WDrawer = WDrawer;
if (!customElements.get('w-drawer')) customElements.define('w-drawer', WDrawer);

if (!window.__wDrawerInit) {
  window.__wDrawerInit = true;
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-w-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-w-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });
}
